import '@/../i18n';
import App from '@/pages/app';
import Forgot from '@/pages/forgot';
import GUI from '@/pages/gui';
import Index from '@/pages/index';
import JobList from '@/pages/jobList';
import Jupyter from '@/pages/jupyter';
import LabPage from '@/pages/lab';
import Login from '@/pages/login';
import Register from '@/pages/register';
import Verification from '@/pages/verification';
import ShareAcceptPage from '@/pages/shareAccept';
import ShareManagementPage from '@/pages/shareManagement';
import CommandPaletteProvider from '@/components/commandPalette/CommandPaletteProvider';
import { App as AntdApp, ConfigProvider } from 'antd';
import { AliveScope, KeepAlive } from 'react-activation';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from "react-router";
import { lightTheme, darkTheme } from '../antd.config';
import './index.css';
import { initializePermissions } from './utils/permissionsService';
import { checkAndUpdateVersion } from './utils/verisonCheck';
import CoursePage from './pages/course';
import { useThemeStore, applyThemeToDOM } from './utils/themeStore';
import { useEffect, type ReactNode } from 'react';
import { setAntdApp } from './utils/antdApp';
import { loadAuthCapabilities, useAuthCapabilities } from './utils/authCapabilities';

const versionMatches = checkAndUpdateVersion(APP_VERSION, 'app_version');
if (!versionMatches) {
  window.location.href = (import.meta.env.VITE_ROUTE_BASE ?? "") + '/login';
}

if (localStorage.getItem('token') && ['/login', '/register'].includes(window.location.pathname)) {
  window.location.href = (import.meta.env.VITE_ROUTE_BASE ?? "") + '/';
}

window.addEventListener("storage", (e) => {
  if (e.key === "token") {
    window.location.reload();
  }
});

initializePermissions();

// Fetched once at startup; the login page and the route guards subscribe to the
// result. Fire-and-forget: a failure leaves the permissive defaults in place and
// the backend still enforces every restriction.
void loadAuthCapabilities();

// 清理旧格式的 auth_token cookie（无 Domain/Secure 属性），改写为带完整属性的版本
// 处理存量用户浏览器中遗留的旧 cookie，防止发送两份 auth_token 给后端
if (localStorage.getItem('token') && localStorage.getItem('cookieConsent') === 'true') {
  const rootDomain = location.hostname.split('.').slice(-2).join('.');
  document.cookie = `auth_token=;Path=/;Expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  document.cookie = `auth_token=;Path=/;Domain=.${rootDomain};Expires=Thu, 01 Jan 1970 00:00:00 GMT;Secure;SameSite=None`;
  document.cookie = `auth_token=${localStorage.getItem('token')};Path=/;Domain=.${rootDomain};Secure;SameSite=None`;
}

window.addEventListener('dragover', (e) => e.preventDefault(), false);
window.addEventListener('drop', (e) => e.preventDefault(), false);

const AntdAppProvider = () => {
  const app = AntdApp.useApp();
  useEffect(() => {
    setAntdApp(app);
  }, [app]);
  return null;
};

/**
 * Guards a self-service route. When the deployment does not offer the flow,
 * rendering the form would only produce a "read-only in this mode" error after
 * the user filled it in -- and each of these flows depends on an emailed
 * verification code that a synthesized address can never receive.
 */
const requireSelfService = (allowed: boolean, element: ReactNode) =>
  allowed ? <>{element}</> : <Navigate to="/login" replace />;

const ThemedApp = () => {
  const theme = useThemeStore((state) => state.theme);
  const authCaps = useAuthCapabilities();

  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  const antdTheme = theme === 'dark' ? darkTheme : lightTheme;

  return (
    <ConfigProvider theme={antdTheme}>
      <AntdApp style={{height: '100%', width: '100%', overflow:'hidden'}}>
        <AntdAppProvider />
        <BrowserRouter basename={import.meta.env.VITE_ROUTE_BASE ?? ""}>
          <CommandPaletteProvider>
            <AliveScope>
              <Routes>
                <Route element={<App />} >
                  <Route index element={<KeepAlive cacheKey="index"><Index /></KeepAlive>} />
                  <Route path="/jupyter" element={<KeepAlive cacheKey="jupyter"><Jupyter /></KeepAlive>} />
                  <Route path="/jobList" element={<JobList />} />
                  <Route path="/share/management" element={<ShareManagementPage />} />
                  <Route path="/course" element={<KeepAlive cacheKey="course"><CoursePage /></KeepAlive>} />
                  <Route path="/gui" element={<KeepAlive cacheKey="gui"><GUI /></KeepAlive>} />
                  <Route path="/lab" element={<KeepAlive cacheKey="lab"><LabPage /></KeepAlive>} />
                </Route>
                <Route path="/share/accept/:shareId" element={<ShareAcceptPage />} />
                <Route path="/forgot-password" element={requireSelfService(authCaps.self_service.password_reset, <Forgot />)} />
                <Route path="/verification" element={requireSelfService(authCaps.self_service.access_verification, <Verification />)} />
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={requireSelfService(authCaps.self_service.registration, <Register />)} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AliveScope>
          </CommandPaletteProvider>
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
};

createRoot(document.getElementById('root')!).render(<ThemedApp />);
